import Big from 'big.js';
import React, { useState, useMemo, useLayoutEffect, useCallback, startTransition } from 'react';
import cx from 'classnames';
import { FaCheckCircle, FaEdit, FaExchangeAlt, FaMapMarkedAlt, FaMoneyBillWave, FaShoppingCart, FaTasks, FaTruck, FaUndo, FaUser } from 'react-icons/fa';
import * as countryCodes from 'country-codes-list';
import { gql, useMutation, useSuspenseQuery } from '@apollo/client';
import Button from '../common/Button';
import { Tabs } from '../common/Tabs';
import { TextInput } from '../common/TextInput';
import dayjs from 'dayjs';
import { getPlatformColor } from '../../utils/getPlatformColor';
import { Outlet, useParams } from 'react-router-dom';
import { geocodeAddress } from '../../utils/GoogleMapsAPI';
import { OrderPageLineItems, SUPPLIER_PRODUCT_VARIATION_FRAGMENT } from './OrderPageLineItems';
import { formatCurrency } from '../../utils/currency';
import OrderPageFulfillments from './OrderPageFulfillments';
import { postMessageToLeboncoinExt } from '../../lib/leboncoin/LeboncoinExtApi';
import { OrderPage_GetExistingOrderQuery, OrderPage_GetExistingOrderQueryVariables, OrderPage_SyncFromExternalOrderMutation, OrderPage_SyncFromExternalOrderMutationVariables, OrderPage_UpdateOrderMutation, OrderPage_UpdateOrderMutationVariables, TransactionType, UpdateOrderInput } from '../../../codegen/graphql';
import { Screen, useScreenContext } from '../Screen';
import FulfillmentStatus from '../common/FulfillmentStatus';
import { useRequestManager } from '../common/RequestManager';
import { AliexpressAddressMatchingModal } from './AliexpressAddressMatchingModal';
import { DropdownMenu } from '../common/DropdownMenu';
import CreateFullfillmentModal from './BuyFulFillmentModal';
import { useModalRouter } from '../../ModalRouter_context';
import OrderPageTransactions, { ORDER_PAGE_TRANSACTIONS_ORDER_FRAGMENT } from './OrderPageTransactions';
import OrderPageAfterSales from './OrderPageAfterSales';
import OrderPageVerifyDataIntegrity, { OrderPageVerifyDataIntegrity_Order } from './OrderPageVerifyDataIntegrity';
import OrderPageProcesses from './OrderPageProcesses';
import { PROCESS_ROW_FRAGMENT } from '../Processes/ProcessRow';
import { getTranslatedValue, Translations } from '../common/TranslatedValue';


/* The order page */

const OrderPage: React.FC = () => {
  // Route parameters
  const { orderId } = useParams();
  if (!orderId) {
    throw new Error('Order ID is required');
  }

  // Global helpers
  const screenContext = useScreenContext();
  const modalRouter = useModalRouter();
  const { trackRequest } = useRequestManager();

  // Main query
  const { data: orderDataRaw, refetch: refetchOrderDataRaw } = useSuspenseQuery<
    OrderPage_GetExistingOrderQuery,
    OrderPage_GetExistingOrderQueryVariables
  >(ORDER_PAGE_GET_ORDER_QUERY, {
    fetchPolicy: 'cache-and-network',
    variables: {
      orderId,
    },
  });
  const orderData = orderDataRaw.order;
  if (!orderData) {
    throw new Error('Order not found');
  }

  // Quick function to refresh data with UI transition
  const refetchOrderData = useCallback(() => {
    startTransition(() => {
      refetchOrderDataRaw();
    });
  }, [refetchOrderDataRaw]);


  // General update order mutation
  const [updateOrderMutation] = useMutation<
    OrderPage_UpdateOrderMutation,
    OrderPage_UpdateOrderMutationVariables
  >(ORDER_PAGE_UPDATE_ORDER_MUTATION);

  // Wrapped update order with safeguards and notifications
  const updateOrder = useCallback(async (input: UpdateOrderInput) => {
    for (const [key, value] of Object.entries(input)) {
      if (value === undefined) {
        delete input[key as keyof UpdateOrderInput];
      }
    }

    trackRequest(
      updateOrderMutation({
        variables: {
          id: orderId,
          input,
        },
      }),
      {
        title: 'Updating order',
        isBlocking: false,
      }
    )
  }, [updateOrderMutation, orderId, trackRequest]);


  // Resync data from external order
  const [syncFromExternalOrder] = useMutation<
    OrderPage_SyncFromExternalOrderMutation,
    OrderPage_SyncFromExternalOrderMutationVariables
  >(ORDER_PAGE_SYNC_FROM_EXTERNAL_ORDER_MUTATION);

  const resyncExternalOrder = useCallback(async () => {
    let externalOrderData = null;
    if (orderData.shop.shopType === 'leboncoin') {
      externalOrderData = await postMessageToLeboncoinExt(window, {
        action: 'get-single-order',
        orderId: orderData.orderId!,
      });
    }

    await trackRequest(
      syncFromExternalOrder({
        variables: {
          shopId: orderData.shopId,
          shopType: orderData.shop.shopType,
          externalOrderId: orderData.orderId!,
          externalOrderLang: orderData.customerAddressCountryCode,
          externalOrderData,
        },
      }),
      {
        title: 'Syncing order',
        isBlocking: false,
      },
    );
  }, [orderData]);


  // UI Component state
  const [leftMenuActiveTab, setLeftMenuActiveTab] = useState('customer');
  const [isAddressMatchingModalOpen, setIsAddressMatchingModalOpen] = useState(false);
  const [isFulfillmentModalOpen, setIsFulfillmentModalOpen] = useState(false);

  // Address form state
  const [addressForm, setAddressForm] = useState({
    customerName: '',
    customerEmail: '',
    customerAddressPhoneCountryCode: '',
    customerAddressPhone: '',
    customerAddressStreetNumber: '',
    customerAddressLine1: '',
    customerAddressLine2: '',
    customerAddressCity: '',
    customerAddressState: '',
    customerAddressPostalCode: '',
    customerAddressCountryCode: '',
  });

  // Initialize and Refresh Address Form state with order data
  useLayoutEffect(() => {
    setAddressForm({
      customerName: orderData.customerName || '',
      customerEmail: orderData.customerEmail || '',
      customerAddressPhoneCountryCode: orderData.customerAddressPhoneCountryCode || '',
      customerAddressPhone: orderData.customerAddressPhone || '',
      customerAddressStreetNumber: orderData.customerAddressStreetNumber || orderData.customerOriginalAddressStreetNumber || '',
      customerAddressLine1: orderData.customerAddressLine1 || orderData.customerOriginalAddressLine1 || '',
      customerAddressLine2: orderData.customerAddressLine2 || orderData.customerOriginalAddressLine2 || '',
      customerAddressCity: orderData.customerAddressCity || orderData.customerOriginalAddressCity || '',
      customerAddressState: orderData.customerAddressState || orderData.customerOriginalAddressState || '',
      customerAddressPostalCode: orderData.customerAddressPostalCode || orderData.customerOriginalAddressPostalCode || '',
      customerAddressCountryCode: orderData.customerAddressCountryCode || orderData.customerOriginalAddressCountryCode || '',
    });
  }, [orderData]);
  
  const onFulfillButtonClick = useCallback(async () => {
    setIsFulfillmentModalOpen(true);
  }, []);

  // Refresh main navigation UI based on order data
  useLayoutEffect(() => {
    const totalOrderLineItemsQuantity = orderData.orderProductsVariations.reduce((acc: number, orderProductVariation: any) => acc + orderProductVariation.quantity, 0) || 0;
    const totalOrderLineItemsPrice = orderData.transactions.reduce((acc: { grossSale: Big, grossMargin: Big }, transaction: any) => {
      if (transaction.type === TransactionType.GrossSale) {
        return {
          grossSale: acc.grossSale.plus(transaction.amount),
          grossMargin: acc.grossMargin.plus(transaction.amount).minus(transaction.amountVat),
        };
      }
      else {
        return {
          ...acc,
          grossMargin: acc.grossMargin.plus(transaction.amount).minus(transaction.amountVat),
        }
      }
    }, { grossSale: new Big(0), grossMargin: new Big(0) }) || 0;
  
    const title = (
      <>
        <div className="flex flex-row gap-2 items-center">
          <FulfillmentStatus status={orderData.orderStatus} className="text-lg" />

          <a
            href={externalOrderLink}
            className={cx(
              getPlatformColor(orderData.shop.shopType).textClass,
              `font-medium underline`
            )}
            target="_blank"
          >
            {orderData.shop.shopType}
            #{orderData.orderId}
          </a>
          <span>
            {dayjs(orderData.orderDate).format('DD ddd. MMMM, HH:mm')}
          </span>
        </div>
        <div className="flex flex-row gap-2">
          {orderData.orderProductsVariations.map((orderProductVariation) => (
            `${getTranslatedValue(orderProductVariation.adVariation?.ad.title as unknown as Translations, orderData.customerAddressCountryCode)} x ${orderProductVariation.quantity}`
          )).join(', ')}
        </div>
      </>
    );
    screenContext.nav.setTitle(title);
    screenContext.nav.setTitleText(`${orderData.shop.shopType} #${orderData.orderId}`);
    screenContext.nav.setTitleRightSide(
      <Button
        label="Resync external order"
        preset="white"
        onClick={resyncExternalOrder}
      />
    );
    screenContext.nav.setNavSteps({
      navSteps: {
        lineItems: {
          label: <>
            {totalOrderLineItemsQuantity} item{totalOrderLineItemsQuantity > 1 ? 's' : ''}&nbsp;
            / {formatCurrency(totalOrderLineItemsPrice.grossSale.round(2).toNumber())}&nbsp;
            <span className={cx(totalOrderLineItemsPrice.grossMargin.gt(0) ? 'text-green-700' : 'text-red-700')}>
              {formatCurrency(totalOrderLineItemsPrice.grossMargin.round(2).toNumber())}
              <span className="text-xs">HT</span>
            </span>
          </>,
          icon: <FaShoppingCart />,
        },
        fulfillments: {
          label: 'Fulfillments',
          icon: <FaTruck />,
        },
        transactions: {
          label: `Transactions (${orderData.transactions?.length || 0})`,
          icon: <FaMoneyBillWave />,
        },
        afterSales: {
          label: `After Sales (${orderData.afterSales?.length || 0})`,
          icon: <FaExchangeAlt />,
        },
        dataIntegrity: {
          label: 'Data Integrity',
          icon: <FaCheckCircle />,
        },
        processes: {
          label: `Processes (${orderData.processes?.length || 0})`,
          icon: <FaTasks />,
        },
      },
      defaultNavStep: 'lineItems',
      defaultSubStep: null,
    });

    const hasAllAliexpressSuppliers = (
      orderData.orderProductsVariations.every((orderProductVariation) => (
        orderProductVariation.supplierProductVariation?.supplierProduct.supplierName === 'aliexpress'
      ))
    )
    const hasNoFulfillments = orderData.orderFulfillments.length === 0;

    let disabledPlaceOrder = !hasAllAliexpressSuppliers || !hasNoFulfillments;
    let disabledReason;
    if (!hasAllAliexpressSuppliers) {
      disabledReason = 'All suppliers must be Aliexpress';
    }
    if (!hasNoFulfillments) {
      disabledReason = 'This order has already been fulfilled';
    }

    screenContext.nav.setActions([
      {
        label: 'Fulfill',
        onClick: onFulfillButtonClick,
        preset: 'primary',
        // disabled: disabledPlaceOrder,
        // tooltip: disabledReason,
      }
    ]);
  }, [orderData, resyncExternalOrder, onFulfillButtonClick]);

  // Event handlers  
  const handleAddressInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setAddressForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddressInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    updateOrder({
      [name]: value
    });
  };
  
  const handleSelectAliexpressAddress = useCallback(async (selectedAddress: any) => {
    let newCity = undefined;
    let newState = undefined;
    let newAddressLine2 = undefined;
    if (selectedAddress.type === 'TOWN') {
      newAddressLine2 = addressForm.customerAddressLine2
        ? `${addressForm.customerAddressLine2}, ${selectedAddress.name}`
        : selectedAddress.name;
      newCity = selectedAddress.parent.name;
      newState = selectedAddress.parent.parent.name;
    } else if (selectedAddress.type === 'CITY') {
      newCity = selectedAddress.name;
      newState = selectedAddress.parent.name;
    }

    updateOrder({ 
      customerAddressCity: newCity,
      customerAddressState: newState,
      customerAddressLine2: newAddressLine2,
    });
  }, [orderId, updateOrder]);

  // External order link
  const externalOrderLink = useMemo(() => {
    if (orderData.shop.shopType === 'ebay') {
      return `https://www.ebay.fr/mesh/ord/details?orderid=${orderData.orderId}`;
    }

    if (orderData.shop.shopType === 'temu') {
      return `https://seller-eu.temu.com/order-detail.html?parent_order_sn=${orderData.orderId}`;
    }

    return '';
  }, [orderData.shop.shopType, orderData.orderId]);

  const restoreOriginalAddress = useCallback(async () => {
    const restoredAddress = {
      customerAddressStreetNumber: orderData.customerOriginalAddressStreetNumber,
      customerAddressLine1: orderData.customerOriginalAddressLine1,
      customerAddressLine2: orderData.customerOriginalAddressLine2,
      customerAddressCity: orderData.customerOriginalAddressCity,
      customerAddressState: orderData.customerOriginalAddressState,
      customerAddressPostalCode: orderData.customerOriginalAddressPostalCode,
      customerAddressCountryCode: orderData.customerOriginalAddressCountryCode,
    };
    
    setAddressForm(prev => ({
      ...prev,
      customerAddressStreetNumber: restoredAddress.customerAddressStreetNumber || '',
      customerAddressLine1: restoredAddress.customerAddressLine1 || '',
      customerAddressLine2: restoredAddress.customerAddressLine2 || '',
      customerAddressCity: restoredAddress.customerAddressCity || '',
      customerAddressState: restoredAddress.customerAddressState || '',
      customerAddressPostalCode: restoredAddress.customerAddressPostalCode || '',
      customerAddressCountryCode: restoredAddress.customerAddressCountryCode || '',
    }));
    
    updateOrder(restoredAddress);
  }, [orderData, updateOrder]);

  const validateAndEnhanceAddress = useCallback(async () => {
    // validateAddress(
    //   [
    //     addressForm.name,
    //     addressForm.addressLine1 + ', ' + addressForm.streetNumber,
    //     addressForm.addressLine2,
    //     addressForm.city,
    //     addressForm.stateOrProvince,
    //     addressForm.postalCode,
    //   ],
    //   addressForm.countryCode,
    //   addressForm.postalCode
    // )
    const geocodeAddressData = await geocodeAddress(
      [
        orderData.customerOriginalAddressStreetNumber,
        orderData.customerOriginalAddressLine1,
        orderData.customerOriginalAddressLine2,
        orderData.customerOriginalAddressCity,
        orderData.customerOriginalAddressState,
        orderData.customerOriginalAddressPostalCode,
        orderData.customerOriginalAddressCountryCode,
      ].filter(Boolean).join(', '),
    )

    const result = geocodeAddressData.results[0];
    const streetNumberData = result.address_components.find(
      (c: any) => c.types.includes('street_number')
    )
    const addressLine1Data = result.address_components.find(
      (c: any) => c.types.includes('route')
    )
    // const addressCityData = result.address_components.find(
    //   (c: any) => c.types.includes('administrative_area_level_3')
    // )
    const addressProvinceData = result.address_components.find(
      (c: any) => c.types.includes('administrative_area_level_2')
    )

    // const postalCodeData = result.address_components.find(
    //   (c: any) => c.types.includes('postal_code')
    // )

    const updates: Partial<UpdateOrderInput> = {
      customerAddressStreetNumber: streetNumberData?.long_name || undefined,
      customerAddressLine1: addressLine1Data?.long_name || undefined,
      // city: addressCityData?.long_name || undefined,
      customerAddressState: addressProvinceData?.long_name || undefined,
      // postalCode: postalCodeData?.long_name || undefined,
    }

    for (const [key, value] of Object.entries(updates)) {
      if (value === undefined) {
        delete updates[key as keyof UpdateOrderInput];
      }
    }

    // Update state with the enhanced address data
    setAddressForm(prev => ({
      ...prev,
      customerAddressStreetNumber: updates.customerAddressStreetNumber || '',
      customerAddressLine1: updates.customerAddressLine1 || '',
      customerAddressState: updates.customerAddressState || '',
    }));

    updateOrder(updates);
  }, []);

  const orderCountryData = countryCodes.findOne('countryCode', orderData.customerAddressCountryCode);


  return (<>
    {/* External Order Details Section */}
      {/* Address Section */}
      <div className="grid grid-cols-12 gap-4 px-4 py-4 pb-40">
        <div className="col-span-3">
          <Tabs
            className="mr-1"
            tabs={[{
              id: 'customer',
              label: 'Customer',
              icon: <FaUser />,
            }, {
              id: 'tasks',
              label: 'Tasks',
              icon: <FaTasks />,
              badge: orderData.tasks.reduce((acc: number, task: any) => acc + (task.completed ? 0 : 1), 0),
            }]}
            activeTab={leftMenuActiveTab}
            onTabChange={(tabId) => {
              setLeftMenuActiveTab(tabId);
            }}
          />

          {leftMenuActiveTab === 'customer' && (
            <div className=" bg-white rounded-lg rounded-tl-none border-t-0 flex flex-col mb-4 border-1 border-gray-400 p-2">
              {/* Address Header */}
              <div className="flex flex-row items-center">
                <p className="text-xl flex-1 ml-4">
                  <span>{dayjs(orderData.orderDate).format('DD ddd. MMMM, HH:mm')}</span>{' '}
                  <a
                    href={externalOrderLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cx(
                      getPlatformColor(orderData.shop.shopType).textClass,
                      "underline",
                      "break-all"
                    )}
                  >
                    {orderData.shop.shopType}<br/>
                    #{orderData.orderId}<br/>
                    {orderData.shop.title}
                  </a>
                  <br/>
                  <span className="mr-2">
                    <span className="mr-2">{orderCountryData?.flag}</span>
                    <span>{orderCountryData?.countryNameEn}</span>
                  </span>
                  <span className="font-bold">{orderData.customerName}</span>
                </p>
              </div>


              {/* Address Form */}
              <h3 className="text-lg font-medium text-gray-900 mb-2 mt-4">Customer Details</h3>
              <TextInput
                name="customerName"
                required
                value={addressForm.customerName}
                onChange={handleAddressInputChange}
                onBlur={handleAddressInputBlur}
                className="mb-4 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-xs lg:text-lg border-gray-300 rounded-md"
              />

              <div className="flex flex-row gap-2">
                <TextInput
                  name="customerAddressPhoneCountryCode"
                  value={addressForm.customerAddressPhoneCountryCode}
                  onChange={handleAddressInputChange}
                  onBlur={handleAddressInputBlur}
                  containerClassName="mb-4 flex-none"
                  className="w-15"
                  LeftAccessory={<span className="text-lg">+</span>}
                  fullWidth={false}
                />
                <TextInput
                  name="customerAddressPhone"
                  value={addressForm.customerAddressPhone}
                  onChange={handleAddressInputChange}
                  onBlur={handleAddressInputBlur}
                  containerClassName="mb-4 flex-1"
                />
              </div>
              <TextInput
                name="customerEmail"
                type="text"
                value={addressForm.customerEmail}
                onChange={handleAddressInputChange}
                onBlur={handleAddressInputBlur}
                containerClassName="mb-4 flex-1"
              />
              <h3 className="text-lg font-medium text-gray-900 mb-2 flex flex-row gap-2 items-center">
                Shipping Addres
              </h3>

              <div className="flex flex-row gap-2 mb-2">
                <DropdownMenu
                  label="Addresses actions"
                  menuPlacement="left"
                  actions={[
                    {
                      label: 'Match against Aliexpress addresses',
                      icon: <FaMapMarkedAlt size={20} />,
                      onClick: () => {
                        setIsAddressMatchingModalOpen(true);
                      },
                    },
                    {
                      label: 'Enhance with Google Maps',
                      icon: <FaMapMarkedAlt size={20} />,
                      onClick: () => {
                        validateAndEnhanceAddress();
                      },
                    },
                    {
                      label: 'Open Maps',
                      icon: <FaMapMarkedAlt size={20} />,
                      onClick: () => {
                        const addressQuery = encodeURIComponent(`${addressForm.customerAddressStreetNumber}, ${addressForm.customerAddressLine1}, ${addressForm.customerAddressCity} ${addressForm.customerAddressPostalCode}, ${addressForm.customerAddressState}, ${addressForm.customerAddressCountryCode}`);
                        window.open(`https://maps.google.com/?q=${addressQuery}&z=3`, '_blank');
                      },
                    },
                    {
                      label: 'Restore',
                      icon: <FaUndo size={20} />,
                      onClick: () => {
                        restoreOriginalAddress();
                      },
                    },
                  ]}
                />
              </div>

              <div className="flex flex-row gap-2">

                <TextInput
                  type="text"
                  helpText={
                    addressForm.customerAddressStreetNumber?.toLowerCase() !== orderData.customerOriginalAddressStreetNumber?.toLowerCase()
                      ? <span>{orderData.customerOriginalAddressStreetNumber || '(empty)'}</span>
                      : undefined
                  }
                  helpTextClassName="text-yellow-700 text-md mt-1"
                  name="customerAddressStreetNumber"
                  label="N*"
                  required
                  value={addressForm.customerAddressStreetNumber}
                  onChange={handleAddressInputChange}
                  onBlur={handleAddressInputBlur}
                  containerClassName="w-15"
                />

                <TextInput
                  type="text"
                  helpText={
                    addressForm.customerAddressLine1?.toLowerCase() !== orderData.customerOriginalAddressLine1?.toLowerCase()
                      ? <span>{orderData.customerOriginalAddressLine1 || '(empty)'}</span>
                      : undefined
                  }
                  helpTextClassName="text-yellow-700 text-md mt-1"
                  name="customerAddressLine1"
                  label="Address Line 1"
                  required
                  value={addressForm.customerAddressLine1}
                  onChange={handleAddressInputChange}
                  onBlur={handleAddressInputBlur}
                  containerClassName="flex-1 mb-2"
                />
              </div>

              <TextInput
                type="text"
                name="customerAddressLine2"
                label="Address Line 2"
                helpText={
                  addressForm.customerAddressLine2?.toLowerCase() !== orderData.customerOriginalAddressLine2?.toLowerCase()
                    ? <span>{orderData.customerOriginalAddressLine2 || '(empty)'}</span>
                    : undefined
                }
                helpTextClassName="text-yellow-700 text-md mt-1"
                required
                value={addressForm.customerAddressLine2}
                onChange={handleAddressInputChange}
                onBlur={handleAddressInputBlur}
                containerClassName="flex-1 mb-2"
              />

              <div className="grid grid-cols-2 gap-2 mb-2">
                <TextInput
                  type="text"
                  name="customerAddressCity"
                  label="City"
                  helpText={
                    addressForm.customerAddressCity?.toLowerCase() !== orderData.customerOriginalAddressCity?.toLowerCase()
                      ? <span>{orderData.customerOriginalAddressCity || '(empty)'}</span>
                      : undefined
                  }
                  helpTextClassName="text-yellow-700 text-md mt-1"
                  required
                  value={addressForm.customerAddressCity}
                  onChange={handleAddressInputChange}
                  onBlur={handleAddressInputBlur}
                  containerClassName="flex-1"
                />

                <TextInput
                  type="text"
                  name="customerAddressState"
                  label="State / Province"
                  helpText={
                    addressForm.customerAddressState?.toLowerCase() !== orderData.customerOriginalAddressState?.toLowerCase()
                    ? <span>{orderData.customerOriginalAddressState || '(empty)'}</span>
                    : undefined
                  }
                  helpTextClassName="text-yellow-700 text-md mt-1"
                  value={addressForm.customerAddressState}
                  onChange={handleAddressInputChange}
                  onBlur={handleAddressInputBlur}
                  containerClassName="flex-1"
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <TextInput
                  type="text"
                  name="customerAddressPostalCode"
                  label="Postal Code"
                  required
                  helpText={
                    addressForm.customerAddressPostalCode?.toLowerCase() !== orderData.customerOriginalAddressPostalCode?.toLowerCase()
                      ? <span>{orderData.customerOriginalAddressPostalCode || '(empty)'}</span>
                      : undefined
                  }
                  helpTextClassName="text-yellow-700 text-md mt-1"
                  value={addressForm.customerAddressPostalCode}
                  onChange={handleAddressInputChange}
                  onBlur={handleAddressInputBlur}
                  containerClassName="flex-1"
                />
              </div>
            </div>
          )}

          {leftMenuActiveTab === 'tasks' && (<>
            {orderData.tasks && orderData.tasks.map((task) => (
              <div className={cx(
                "rounded-lg flex flex-col items-start mb-4 border-1 p-2",
                task.completed ? "bg-green-50 border-green-300" : "bg-orange-50 border-orange-300"
              )}>
                <p
                  className={cx(
                    "text-sm mb-1",
                    task.completed ? "text-green-800" : "text-red-500"
                  )}
                >
                  Crée le {dayjs(task.createdAt).format('DD ddd. MMMM, HH:mm')}
                </p>
                {!task.completed && <p className="text-sm text-red-500 mb-1">Prochaine action le {dayjs(task.nextDueDate).format('DD ddd. MMMM, HH:mm')}</p>}
                <h3 className="text-lg font-medium text-gray-900 mb-2 flex flex-row items-center">
                  {task.completed && <FaCheckCircle className="mr-2 text-green-500" />}
                  {task.title}
                </h3>
                <p className="text-md text-gray-500 mb-2">{task.description}</p>
                <Button
                  preset="white"
                  onClick={() => {
                    modalRouter.push({
                      s: 'task',
                      a: {
                        taskId: task.id,
                      },
                    });
                  }}
                >
                  <FaEdit className="mr-2" size={20} />
                  Open task
                </Button>
              </div>
            ))}

            {orderData.tasks && orderData.tasks.length === 0 && (
              <div className="rounded-lg mb-4 border-2 border-dashed border-gray-300 p-2">
                <p className="text-md text-center text-gray-500 mb-1">No tasks found</p>
              </div>
            )}
            {orderData.tasks && (
              <Button
                preset="white"
                onClick={() => {
                  modalRouter.push({
                    s: 'task',
                    a: {
                      taskId: 'new',
                      newTaskOrderId: orderData.id,
                    },
                  });
                }}
              >
                Create task
              </Button>
            )}
          </>)}
        </div>


        <div className="col-span-9">
          {/* Line Items Section */}
          {screenContext.nav.currentNavStep === 'lineItems' && (
            <OrderPageLineItems
              orderData={orderData}
              addressForm={addressForm}
              onRefresh={refetchOrderData}
            />
          )}
          {/* Fulfillments Section */}
          {screenContext.nav.currentNavStep === 'fulfillments' && (
            <OrderPageFulfillments
              orderData={orderData}
            />
          )}
          {/* Processes Section */}
          {screenContext.nav.currentNavStep === 'processes' && (
            <OrderPageProcesses
              orderData={orderData}
            />
          )}

          {screenContext.nav.currentNavStep === 'transactions' && (
            <OrderPageTransactions
              order={orderData}
            />
          )}

          {screenContext.nav.currentNavStep === 'afterSales' && (
            <OrderPageAfterSales
              order={orderData}
            />
          )}

          {screenContext.nav.currentNavStep === 'dataIntegrity' && (
            <OrderPageVerifyDataIntegrity
              order={orderData}
            />
          )}
        </div>
      </div>

      {isAddressMatchingModalOpen && <AliexpressAddressMatchingModal
        onClose={() => setIsAddressMatchingModalOpen(false)}
        onSelect={handleSelectAliexpressAddress}
        currentAddress={addressForm}
        countryCode={addressForm.customerAddressCountryCode}
      />}

      {/* Fulfillment Modal */}
      {isFulfillmentModalOpen && (
        <CreateFullfillmentModal 
          orderId={orderData.id} 
          onClose={() => {
            setIsFulfillmentModalOpen(false);
          }}
          onSuccess={() => {
            setIsFulfillmentModalOpen(false);
          }}
        />
      )}

      <Outlet />
    </>);
};

export default function OrderPageWrapper(props: any) {
  const { orderId } = useParams();
  return (
    <Screen key={orderId}>
      <OrderPage {...props} />
    </Screen>
  );
}

/*
 * Data
 */
export const ORDER_ITEM_VARIATION_FRAGMENT = gql`
  fragment OrderPage_AdVariation on AdVariation {
    id
    mainImage {
      id
      url
    }
    ad {
      id
      title
      typeIdUsedForImages

      variations {
        id
        variationTypeValues {
          id
          value
          type {
            id
            type
          }
        }
      }

      variationTypes {  
        id
        type
        typeValues {
          id
          value
          medias {
            id
            url
          }
        }
      }
    }

    selectedSupplierProductVariation {
      sku
      supplierProductId
    }
    lastUsedSupplierProductVariation {
      sku
      supplierProductId
    }

    supplierProductVariations {
      ...OrderPage_SupplierProductVariation @unmask
    }

    variationTypeValues {
      id
      type {
        id
        type
      }
      value
      medias {
        id
        url
      }
    }
  }
  ${SUPPLIER_PRODUCT_VARIATION_FRAGMENT}
`;

export const ORDER_PAGE_ORDER_FRAGMENT = gql`
  fragment OrderPage_Order on Order {
    id
    orderId
    shopId
    shop {
      shopType
      title
    }
    orderDate
    orderStatus
    originalLineItems {
      name
      lineItemId
      adVariationId
      quantity
      pricePaidByCustomer
    }

    customerName
    customerEmail
    customerAddressStreetNumber
    customerAddressLine1
    customerAddressLine2
    customerAddressCity
    customerAddressPostalCode
    customerAddressCountry
    customerAddressCountryCode
    customerAddressState
    customerAddressPhone
    customerAddressPhoneCountryCode

    customerOriginalAddressStreetNumber
    customerOriginalAddressLine1
    customerOriginalAddressLine2
    customerOriginalAddressCity
    customerOriginalAddressState
    customerOriginalAddressPostalCode
    customerOriginalAddressCountryCode

    orderProductsVariations {
      id
      orderId
      variationId
      quantity
      supplierSku
      extraData
      externalLineItemId

      money {
        currency
        grossSale
        grossSaleVat
        grossMargin
        grossMarginVat
      }

      supplierProductVariation {
        ...OrderPage_SupplierProductVariation @unmask
      }

      restockShipmentProductVariation {
        id
        quantity
        availableStock
        supplierProductVariationSku
        supplierProductVariationSupplierProductId
        cost {
          unitCost
          totalCost
          totalFreightCost
          totalProductCost
        }
        restockShipmentProduct {
          id
          restockShipmentId
          externalOrderId
          sellerCompany
          shippingTrackingNumber
        }
      }

      adVariation {
        ...OrderPage_AdVariation @unmask
      }
    }
    
    orderFulfillments {
      id
      fullfilledOrderId
      fullfilledPlatform
      shippingCarrier
      trackingNumber
      type
      fullfilledStatus
      packaging {
        weight
        size1
        size2
        size3
      }
      createdAt
      updatedAt

      orderProductsVariations {
        id
        quantity
        adVariation {
          id
          ad {
            title
          }
          mainImage {
            id
            url
          }
        }
      }
    }

    tasks {
      id
      title
      description
      nextDueDate
      nextActionContent
      completed
      createdAt
      updatedAt
    }

    processes {
      ...ProcessRow_Process
    }

    afterSales {
      id
      externalId
      type
      status
      orderId
      lineItemId
      lineItemQuantity
      restockedQuantity
      notRestockedReason
      returnPackageId
      reason
      reasonCode
      buyerMessage
      requestedAt
      createdAt
      updatedAt
    }

    ...OrderPageTransactions_Order
    ...OrderPageVerifyDataIntegrity_Order
  }
  ${OrderPageVerifyDataIntegrity_Order}
  ${ORDER_ITEM_VARIATION_FRAGMENT}
  ${SUPPLIER_PRODUCT_VARIATION_FRAGMENT}
  ${PROCESS_ROW_FRAGMENT}
  ${ORDER_PAGE_TRANSACTIONS_ORDER_FRAGMENT}
`;

export const ORDER_PAGE_GET_ORDER_QUERY = gql`
  query OrderPage_GetExistingOrder($orderId: ID!) {
    order(id: $orderId) {
      ...OrderPage_Order
    }
  }
  ${ORDER_PAGE_ORDER_FRAGMENT}
`;

export const ORDER_PAGE_SYNC_FROM_EXTERNAL_ORDER_MUTATION = gql`
  mutation OrderPage_SyncFromExternalOrder($shopId: ID!, $shopType: ID!, $externalOrderId: String!, $externalOrderLang: String!, $externalOrderData: JSON) {
    syncFromExternalOrder(shopId: $shopId, shopType: $shopType, externalOrderId: $externalOrderId, externalOrderLang: $externalOrderLang, externalOrderData: $externalOrderData) {
      process {
        id
      }
    }
  }
  ${ORDER_PAGE_ORDER_FRAGMENT}
`;

export const ORDER_PAGE_UPDATE_ORDER_MUTATION = gql`
  mutation OrderPage_UpdateOrder($id: ID!, $input: UpdateOrderInput!) {
    updateOrder(id: $id, input: $input) {
      ...OrderPage_Order
    }
  }
  ${ORDER_PAGE_ORDER_FRAGMENT}
`;
